import React, { useState } from 'react';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { MUCDANHGIA_API, TIEUCHI_API } from '@app/utils/apiUrl';
import { Post, Get } from '@app/utils/httpProvider';
import { messageError, messageToast, status200, styleSelectDefault } from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import { Button } from '@components';
import { AsyncPaginate } from 'react-select-async-paginate';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';

const schema = yup.object().shape({
    tieuChiId: yup.object().required(messageError.emptyMessage).nullable(),
    tenMucDanhGia: yup.string().required(messageError.emptyMessage).max(255, 'Tên mục đánh giá không được vượt quá 255 ký tự'),
    diemSo: yup.number().required(messageError.emptyMessage).min(0, 'Điểm số không được nhỏ hơn 0'),
});

const MucDanhGiaAdd = () => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);

    const addFunc = useMutation((value) =>
        Post(MUCDANHGIA_API.CREATE_ITEM, null, value, false)
    );

    const handleCreate = async (values) => {
        const model = {
            tieuChiId: values.tieuChiId?.value,
            tenMucDanhGia: values.tenMucDanhGia,
            diemSo: values.diemSo
        };

        addFunc.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200 && res.status !== 201) {
                    toast.error(messageToast.messageToastCreateError);
                } else {
                    setIsLoading(true);
                    toast.success(messageToast.messageToastCreateSuccess);
                    history.push(URI_INTERNAL.MUCDANHGIA_PATH);
                }
            },
            onError: (res) => {
                toast.error(
                    res.message || 'Đã có lỗi xảy ra, vui lòng thử lại !'
                );
            }
        });
    };

    const loadOptionsTieuChi = async (search, loadedOptions, { page }) => {
        const pageSize = 10;
        const res = await Get(TIEUCHI_API.GET_PAGING, null, {
            pageIndex: page,
            pageSize: pageSize,
            keyword: search
        });

        const items = res?.data?.$values ? res.data.$values : res?.data || [];

        return {
            options: items.map(item => ({
                value: item.id || item.Id,
                label: item.tenTieuChi || item.TenTieuChi
            })),
            hasMore: items.length >= pageSize,
            additional: {
                page: page + 1,
            },
        };
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm mới mục đánh giá</Card.Header>
            <Card.Body>
                <Formik
                    validationSchema={schema}
                    onSubmit={(values) => handleCreate(values)}
                    initialValues={{
                        tieuChiId: null,
                        tenMucDanhGia: '',
                        diemSo: 0
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue, setFieldTouched }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Tiêu chí <span className="text-danger">*</span>
                                </Form.Label>
                                <Col sm="10">
                                    <AsyncPaginate
                                        value={values.tieuChiId}
                                        loadOptions={loadOptionsTieuChi}
                                        onChange={(value) => setFieldValue('tieuChiId', value)}
                                        additional={{ page: 1 }}
                                        styles={styleSelectDefault}
                                        placeholder="Chọn tiêu chí"
                                        onBlur={() => setFieldTouched('tieuChiId', true)}
                                        isClearable
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.tieuChiId && (
                                        <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                            {errors.tieuChiId}
                                        </div>
                                    )}
                                </Col>
                            </Form.Group>

                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên mục đánh giá',
                                        name: 'tenMucDanhGia',
                                        value: values.tenMucDanhGia,
                                        onChange: handleChange,
                                        isInvalid: errors.tenMucDanhGia,
                                        error: errors.tenMucDanhGia
                                    },
                                    {
                                        label: 'Điểm số',
                                        name: 'diemSo',
                                        type: 'number',
                                        value: values.diemSo,
                                        onChange: handleChange,
                                        isInvalid: errors.diemSo,
                                        error: errors.diemSo
                                    }
                                ]}
                            />

                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary" disabled={isLoading}>
                                            Thêm
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.MUCDANHGIA_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                {isLoading && <div>Đang lưu...</div>}
            </Card.Body>
        </Card>
    );
};

export default MucDanhGiaAdd;
